import '../App.css';
import {
    Box, Button,
    createTheme, CssBaseline, ThemeProvider,
    Stack, TextField, Typography, useMediaQuery,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    IconButton, Tooltip,
} from "@mui/material";
import {Add, Delete, Dns, ContentCopy, Check} from "@mui/icons-material";
import {useEffect, useState} from "react";
import SideBar from "../Componant/sidebar/Sidebar";
import {listDomains, createDomain, deleteDomain} from "../Utils/domainsApi";

function targetHostname() {
    try {
        return new URL(process.env.REACT_APP_API_URL || 'http://localhost:3033').hostname;
    } catch (e) {
        return 'qrcode-api.alais-studio.fr';
    }
}

function StepBadge({children}) {
    return (
        <Box sx={{
            width: 26, height: 26, borderRadius: '50%',
            bgcolor: 'primary.main', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 13, flexShrink: 0,
        }}>
            {children}
        </Box>
    );
}

function DomainSetupGuide() {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(targetHostname()).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };

    return (
        <Box sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: 2,
            p: {xs: 2, sm: 3}, mb: 3,
        }}>
            <Stack direction="row" spacing={1.5} alignItems="center" mb={2.5}>
                <Dns color="primary"/>
                <Typography variant="subtitle1" fontWeight={600}>Configurer votre domaine</Typography>
            </Stack>

            <Stack spacing={2.5}>
                <Stack direction="row" spacing={2}>
                    <StepBadge>1</StepBadge>
                    <Box sx={{flex: 1, minWidth: 0}}>
                        <Typography variant="body2">
                            Chez votre registrar/DNS, créez un enregistrement <strong>CNAME</strong> pour le
                            sous-domaine que vous voulez utiliser (ex. <code>qr</code>), pointant vers :
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{mt: 1}}>
                            <Typography variant="body2" component="code" sx={{
                                fontFamily: 'monospace', bgcolor: 'action.hover', px: 1.5, py: 0.75,
                                borderRadius: 1, wordBreak: 'break-all', flex: 1, minWidth: 0,
                            }}>
                                {targetHostname()}
                            </Typography>
                            <Tooltip title={copied ? 'Copié !' : 'Copier'}>
                                <IconButton size="small" onClick={handleCopy}>
                                    {copied ? <Check fontSize="small" color="success"/> : <ContentCopy fontSize="small"/>}
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={2}>
                    <StepBadge>2</StepBadge>
                    <Typography variant="body2" sx={{flex: 1}}>
                        Ajoutez le domaine complet ci-dessous (ex. <code>qr.mon-domaine.fr</code>) — il devient
                        alors sélectionnable à la création d'un QR code tracké.
                    </Typography>
                </Stack>

                <Stack direction="row" spacing={2}>
                    <StepBadge>3</StepBadge>
                    <Typography variant="body2" color="text.secondary" sx={{flex: 1}}>
                        Une fois le DNS propagé (jusqu'à 24h), le rattachement final du domaine (certificat HTTPS,
                        configuration du serveur) reste une étape manuelle côté administrateur — contactez-le pour
                        finaliser l'activation.
                    </Typography>
                </Stack>
            </Stack>
        </Box>
    );
}

function DomainsPage() {
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const isComputer = useMediaQuery('(min-width:900px)');
    const [sidebarIsOpen, setSidebarIsOpen] = useState();

    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newDomain, setNewDomain] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const darkTheme = createTheme({
        palette: {
            mode: prefersDarkMode ? 'dark' : 'light',
            background: {default: prefersDarkMode ? '#1A1A1A' : '#f5f5f5'},
            primary: {main: '#FF7233', light: '#FF7233', dark: '#e05a1a'},
        },
        typography: {
            fontFamily: '"Poppins", sans-serif',
            allVariants: {color: prefersDarkMode ? '#cecece' : '#000000FF'},
        },
    });

    const refresh = () => {
        setLoading(true);
        listDomains()
            .then(setDomains)
            .catch((err) => console.error('Erreur lors du chargement des domaines:', err))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        refresh();
    }, []);

    const handleAdd = async () => {
        setError(null);
        if (!newDomain.trim()) return;

        setSaving(true);
        try {
            await createDomain(newDomain.trim().toLowerCase());
            setNewDomain('');
            refresh();
        } catch (err) {
            setError(err.response?.data?.error || 'Erreur lors de l\'ajout du domaine');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (domain) => {
        if (!window.confirm(`Supprimer le domaine "${domain.domain}" ?`)) return;

        try {
            await deleteDomain(domain.id);
            refresh();
        } catch (err) {
            console.error('Erreur lors de la suppression:', err);
            alert('Erreur lors de la suppression du domaine');
        }
    };

    const getContainerClass = () => {
        const classes = [];
        if (!isComputer) {
            classes.push("mobile");
        } else {
            classes.push(sidebarIsOpen ? 'activated' : 'disactivated');
        }
        return classes.join(' ');
    };

    return (
        <div className="App">
            <ThemeProvider theme={darkTheme}>
                <SideBar sidebarIsOpen={sidebarIsOpen} setSidebarIsOpen={setSidebarIsOpen} isComputer={isComputer}/>
                <CssBaseline/>

                <Box paddingTop={'5%'} paddingBottom={'5%'} id='Md-Container' className={getContainerClass()}>
                    <Box sx={{padding: {xs: 2, sm: 4}, margin: '0 auto', width: '100%'}}>
                        <Typography variant="h4" gutterBottom>Domaines personnalisés</Typography>

                        <Box sx={{px: {xs: 0, sm: 2}}}>
                            <DomainSetupGuide/>

                            <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} mb={3}>
                                <TextField
                                    label="Nouveau domaine"
                                    placeholder="qr.mon-domaine.fr"
                                    fullWidth
                                    value={newDomain}
                                    onChange={(e) => setNewDomain(e.target.value)}
                                    error={!!error}
                                    helperText={error}
                                />
                                <Button
                                    variant="contained" color="primary" startIcon={<Add/>}
                                    onClick={handleAdd} disabled={saving}
                                    fullWidth={false}
                                    sx={{flexShrink: 0, width: {xs: '100%', sm: 'auto'}, height: 56}}
                                >
                                    Ajouter
                                </Button>
                            </Stack>

                            {loading ? (
                                <Typography>Chargement...</Typography>
                            ) : domains.length === 0 ? (
                                <Typography color="text.secondary">Aucun domaine enregistré.</Typography>
                            ) : (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Domaine</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {domains.map((domain) => (
                                                <TableRow key={domain.id} hover>
                                                    <TableCell sx={{wordBreak: 'break-all'}}>{domain.domain}</TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title="Supprimer">
                                                            <IconButton onClick={() => handleDelete(domain)}>
                                                                <Delete fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Box>
                </Box>
            </ThemeProvider>
        </div>
    );
}

export default DomainsPage;
